import { useEffect, useRef, useState, type FormEvent } from "react";
import { apiRequest } from "../../shared/api/client";
import { errorMessages } from "./auth-presentation";

export function useAuthWorkspace({ mode = "sign-in" }: { mode?: "sign-in" | "service-unavailable" }) {
  const search = new URLSearchParams(window.location.search);
  const code = search.get("authError") ?? search.get("code") ?? "";
  const error = errorMessages[code];
  const serviceUnavailable = mode === "service-unavailable";
  const corporateReturnTo = `${window.location.pathname}${window.location.search}`;
  const [personnelCode, setPersonnelCode] = useState("");
  const [personnelError, setPersonnelError] = useState("");
  const [personnelBusy, setPersonnelBusy] = useState(false);
  const [formPin, setFormPin] = useState("");
  const [formPinError, setFormPinError] = useState("");
  const [formPinBusy, setFormPinBusy] = useState(false);

  const mounted = useRef(false);
  const epoch = useRef(0);
  const personnelLock = useRef(false);
  const formLock = useRef(false);
  useEffect(() => {
    mounted.current = true;
    epoch.current += 1;
    personnelLock.current = false;
    formLock.current = false;
    setPersonnelBusy(false);
    setFormPinBusy(false);
    return () => { mounted.current = false; epoch.current += 1; };
  }, []);

  async function personnelLogin(event: FormEvent) {
    const generation = epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    event.preventDefault();
    if (!isCurrent() || personnelCode.length !== 6 || personnelLock.current) return;
    personnelLock.current = true;
    setPersonnelBusy(true);
    setPersonnelError("");
    try {
      await apiRequest("/api/production/technical-mobile/login", {
        method: "POST",
        body: JSON.stringify({ personnelCode }),
      });
      if (isCurrent()) window.location.assign("/technical-board?setup=1");
    } catch (loginError) {
      if (!isCurrent()) return;
      setPersonnelError(
        loginError instanceof Error
          ? loginError.message
          : "Personel girişi tamamlanamadı.",
      );
      setPersonnelCode("");
    } finally {
      if (isCurrent()) { personnelLock.current = false; setPersonnelBusy(false); }
    }
  }

  async function formOperatorLogin(event: FormEvent) {
    const generation = epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    event.preventDefault();
    if (!isCurrent() || formPin.length !== 4 || formLock.current) return;
    formLock.current = true;
    setFormPinBusy(true);
    setFormPinError("");
    try {
      await apiRequest("/api/shift-production-form/operator/session", {
        method: "POST",
        body: JSON.stringify({ pin: formPin }),
      });
      const target =
        window.location.pathname === "/vardiya-uretim-formu"
          ? `${window.location.pathname}${window.location.search}`
          : "/vardiya-uretim-formu";
      if (isCurrent()) window.location.assign(target);
    } catch (loginError) {
      if (!isCurrent()) return;
      setFormPinError(
        loginError instanceof Error ? loginError.message : "PIN doğrulanamadı.",
      );
      setFormPin("");
    } finally {
      if (isCurrent()) { formLock.current = false; setFormPinBusy(false); }
    }
  }

  function clearPersonnelLogin() {
    setPersonnelCode("");
    setPersonnelError("");
  }

  function clearFormPinLogin() {
    setFormPin("");
    setFormPinError("");
  }

  return { search, error, serviceUnavailable, corporateReturnTo, personnelCode, setPersonnelCode, personnelError, personnelBusy, formPin, setFormPin, formPinError, formPinBusy, personnelLogin, formOperatorLogin, clearPersonnelLogin, clearFormPinLogin };
}
